import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useEffect, type Dispatch, type SetStateAction } from 'react';
import type {
    SupplierRequestComingType,
    WarehouseComingRowType,
} from '@econtractor/types';
import spinnerTitle from 'components/others/spinnerTitle';
import { FilesCloudUpload } from 'modules/files';
import ComingTable from '../../../OffComing/ComingTable';
import ComingBatches from '../ComingBatches';

interface TabComingProps {
    canStartComing: boolean;
    showDraft: boolean;
    draftComing: WarehouseComingRowType[];
    setDraftComing: Dispatch<SetStateAction<WarehouseComingRowType[]>>;
    isSubmitting: boolean;
    canCredit: boolean;
    pendingCloudFiles: File[];
    setPendingCloudFiles: Dispatch<SetStateAction<File[]>>;
    cloudProgress: number | boolean;
    getMaxCount: (row: WarehouseComingRowType) => number | null;
    onOpenDraft: () => void;
    onCancelDraft: () => void;
    onConduct: () => void;
    comings: SupplierRequestComingType[];
}

const TabComing = ({
    canStartComing,
    showDraft,
    draftComing,
    setDraftComing,
    isSubmitting,
    canCredit,
    pendingCloudFiles,
    setPendingCloudFiles,
    cloudProgress,
    getMaxCount,
    onOpenDraft,
    onCancelDraft,
    onConduct,
    comings,
}: TabComingProps) => {
    const { t } = useTranslation(['Warehouses', 'common']);
    const formDisabled = isSubmitting || cloudProgress !== false;
    const hasPaidRows =
        canCredit && draftComing.some((row) => Boolean(row?.coming?.payment));

    useEffect(() => {
        if (!hasPaidRows && pendingCloudFiles.length) {
            setPendingCloudFiles([]);
        }
    }, [hasPaidRows, pendingCloudFiles.length, setPendingCloudFiles]);

    return (
        <>
            <div className="mb-3">
                {canStartComing && !showDraft ? (
                    <Button
                        size="sm"
                        variant="info"
                        disabled={formDisabled}
                        onClick={(e) => {
                            e.currentTarget.blur();
                            onOpenDraft();
                        }}
                    >
                        <i className="bi bi-box-arrow-in-down me-1"></i>
                        {t('supplierRequests.view.btnFormComing')}
                    </Button>
                ) : null}

                {!canStartComing && !showDraft && !comings.length ? (
                    <p className="text-muted small mb-0">
                        {t('supplierRequests.view.comingEmpty')}
                    </p>
                ) : null}
            </div>

            {showDraft ? (
                <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0 text-muted">
                            {t('supplierRequests.view.draftTitle')}
                        </h6>
                        <Button
                            size="sm"
                            variant="link"
                            className="p-0"
                            disabled={formDisabled}
                            onClick={onCancelDraft}
                        >
                            {t('common:buttons.cancel')}
                        </Button>
                    </div>
                    <div className="mx-n3">
                        <ComingTable
                            aComing={draftComing}
                            setAComing={setDraftComing}
                            comingProcess={formDisabled}
                            showPayment={canCredit}
                            getMaxCount={getMaxCount}
                        />
                    </div>
                    {hasPaidRows ? (
                        <div className="mt-3">
                            <FilesCloudUpload
                                disabled={formDisabled}
                                progress={cloudProgress}
                                files={pendingCloudFiles}
                                setFiles={setPendingCloudFiles}
                                label={t('common:labelAttachments')}
                                buttonLabel={t('common:buttons.upload')}
                                uploadingLabel={t('common:buttons.uploading')}
                            />
                        </div>
                    ) : null}
                    <div className="d-flex justify-content-end mt-2">
                        <Button
                            variant="info"
                            className="min100px"
                            disabled={formDisabled || !draftComing.length}
                            onClick={() => void onConduct()}
                        >
                            {spinnerTitle(
                                <>
                                    <i className="bi bi-box-arrow-in-down fs-14 me-2"></i>
                                    {t('supplierRequests.view.btnApproved')}
                                </>,
                                formDisabled,
                                t('common:buttons.saving'),
                            )}
                        </Button>
                    </div>
                </div>
            ) : null}

            <ComingBatches comings={comings} />
        </>
    );
};

export default TabComing;
